import { useEffect, useState } from 'react';
import type { JourneyNode } from '../../../core/entities/node';
import { validateNode } from '../../../core/policies/node-validation/validate-node';
import { useLocale } from '../../preferences/i18n/LocaleProvider';
import { NodeTypeForm } from './NodeTypeForm';
import { NotesEditor } from './NotesEditor';
import { CustomFieldsPanel } from './custom-fields-panel/CustomFieldsPanel';
import { ValidationHintList } from './ValidationHintList';

export function NodeEditorPanel({
  node,
  onSave,
  onCancel,
  onManageRelationships,
  manageRelationshipsDisabled = false,
  readonly = false,
  saveDisabledReason = '',
  saveLabel = 'Save node'
}: {
  node: JourneyNode;
  onSave: (node: JourneyNode) => void;
  onCancel?: () => void;
  onManageRelationships?: () => void;
  manageRelationshipsDisabled?: boolean;
  readonly?: boolean;
  saveDisabledReason?: string;
  saveLabel?: string;
}) {
  const [draft, setDraft] = useState(node);
  const { locale, t } = useLocale();
  const validation = validateNode(draft);

  useEffect(() => {
    setDraft(node);
  }, [node]);

  return (
    <aside aria-label="Node editor" className="floating-panel" style={{ padding: 16, width: 'min(560px, 92vw)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
      <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h2 style={{ margin: 0 }}>{formatActionLabel(locale, draft.displayName.trim() ? t('edit') : t('create'), draft.type === 'Human' ? t('human') : draft.type === 'Project' ? t('project') : t('occupation'))}</h2>
        {onCancel && (
          <button className="hud-subtle-button" type="button" onClick={onCancel}>
            {t('close')}
          </button>
        )}
      </div>
      <NodeTypeForm node={draft} onChange={setDraft} />
      <NotesEditor value={draft.notes} onChange={(notes) => setDraft({ ...draft, notes })} />
      <CustomFieldsPanel fields={draft.customFields} onChange={(customFields) => setDraft({ ...draft, customFields })} />
      <ValidationHintList hints={validation.hints} />
      {onManageRelationships && (
        <button className="hud-subtle-button" disabled={readonly || manageRelationshipsDisabled} onClick={onManageRelationships} type="button">
          {t('manageRelationships')}
        </button>
      )}
      {readonly && <p>{t('readOnlyViewer')}</p>}
      {saveDisabledReason && <p role="note">{saveDisabledReason}</p>}
      <button className="hud-subtle-button" disabled={readonly || !!saveDisabledReason || !validation.valid} onClick={() => onSave(draft)} type="button">
        {saveLabel}
      </button>
      </div>
    </aside>
  );
}

function formatActionLabel(locale: 'zh-TW' | 'en', action: string, target: string): string {
  return locale === 'zh-TW' ? `${action}${target}` : `${action} ${target}`;
}
